import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, LogOut, Save, Plus, Pencil, Trash2, X, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtErr(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const inputCls =
    "w-full bg-[#0B0F17] border border-bn-line rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-bn-blue focus:ring-2 focus:ring-bn-blue/20 transition-all";
const labelCls = "block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-1.5";

const emptyDouble = () => ({
    date: "",
    status: "won",
    matches: [
        { teams: "", pick: "", odds: "", score: "" },
        { teams: "", pick: "", odds: "", score: "" },
    ],
});

const combined = (d) => {
    const o1 = parseFloat(d.matches?.[0]?.odds);
    const o2 = parseFloat(d.matches?.[1]?.odds);
    return o1 && o2 ? (o1 * o2).toFixed(2) : "—";
};

const DoubleForm = ({ initial, onSave, onCancel, saving }) => {
    const [d, setD] = useState(initial);
    const setM = (i, k, v) => setD({ ...d, matches: d.matches.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)) });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSave(d);
            }}
            className="border border-bn-blue/30 rounded-xl p-5 bg-bn-blue/5 space-y-4"
            data-testid="double-form"
        >
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>DATE (DD.MM.YYYY)</label>
                    <input required className={inputCls} value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} placeholder="13.09.2026" data-testid="double-form-date" />
                </div>
                <div>
                    <label className={labelCls}>RESULT</label>
                    <select className={inputCls} value={d.status} onChange={(e) => setD({ ...d, status: e.target.value })} data-testid="double-form-status">
                        <option value="won">WIN</option>
                        <option value="lost">LOSS</option>
                    </select>
                </div>
            </div>
            {d.matches.map((m, i) => (
                <div key={i} className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 font-mono text-[10px] tracking-[0.2em] text-bn-cyan">MATCH {i + 1}</div>
                    <div className="col-span-2">
                        <input required className={inputCls} placeholder="Team A vs Team B" value={m.teams} onChange={(e) => setM(i, "teams", e.target.value)} data-testid={`double-form-m${i}-teams`} />
                    </div>
                    <input required className={inputCls} placeholder="Pick (1, X, Over 2.5)" value={m.pick} onChange={(e) => setM(i, "pick", e.target.value)} data-testid={`double-form-m${i}-pick`} />
                    <input required className={inputCls} placeholder="Odds (1.85)" value={m.odds} onChange={(e) => setM(i, "odds", e.target.value)} data-testid={`double-form-m${i}-odds`} />
                    <div className="col-span-2">
                        <input required className={inputCls} placeholder="Score (FT 2:1)" value={m.score} onChange={(e) => setM(i, "score", e.target.value)} data-testid={`double-form-m${i}-score`} />
                    </div>
                </div>
            ))}
            <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving} data-testid="double-form-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-xs font-semibold disabled:opacity-60 transition-colors">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} SAVE DOUBLE
                </button>
                <button type="button" onClick={onCancel} data-testid="double-form-cancel" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-slate-300 text-xs font-semibold hover:border-bn-blue/50 transition-colors">
                    <X className="w-3.5 h-3.5" /> CANCEL
                </button>
            </div>
        </form>
    );
};

const emptySingle = () => ({ date: "", status: "won", teams: "", pick: "", odds: "", score: "" });

const singleBadgeCls = (status) =>
    status === "lost"
        ? "bg-red-400/10 text-red-400 border-red-400/30"
        : status === "nomatch"
          ? "bg-slate-500/10 text-slate-400 border-slate-500/30"
          : "bg-bn-green/10 text-bn-green border-bn-green/30";

const SingleForm = ({ initial, onSave, onCancel, saving }) => {
    const [s, setS] = useState(initial);
    const nm = s.status === "nomatch";

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSave(s);
            }}
            className="border border-bn-blue/30 rounded-xl p-5 bg-bn-blue/5 space-y-4"
            data-testid="single-form"
        >
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelCls}>DATE (DD.MM.YYYY)</label>
                    <input required className={inputCls} value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} placeholder="13.09.2026" data-testid="single-form-date" />
                </div>
                <div>
                    <label className={labelCls}>RESULT</label>
                    <select className={inputCls} value={s.status} onChange={(e) => setS({ ...s, status: e.target.value })} data-testid="single-form-status">
                        <option value="won">WIN</option>
                        <option value="lost">LOSS</option>
                        <option value="nomatch">NO MATCH</option>
                    </select>
                </div>
            </div>
            {!nm && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <input required className={inputCls} placeholder="Team A vs Team B" value={s.teams ?? ""} onChange={(e) => setS({ ...s, teams: e.target.value })} data-testid="single-form-teams" />
                    </div>
                    <input required className={inputCls} placeholder="Pick (1, 3+, BTS & 3+)" value={s.pick ?? ""} onChange={(e) => setS({ ...s, pick: e.target.value })} data-testid="single-form-pick" />
                    <input required className={inputCls} placeholder="Odds (2.10)" value={s.odds ?? ""} onChange={(e) => setS({ ...s, odds: e.target.value })} data-testid="single-form-odds" />
                    <div className="col-span-2">
                        <input required className={inputCls} placeholder="Score (FT 2:1)" value={s.score ?? ""} onChange={(e) => setS({ ...s, score: e.target.value })} data-testid="single-form-score" />
                    </div>
                </div>
            )}
            <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving} data-testid="single-form-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-xs font-semibold disabled:opacity-60 transition-colors">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} SAVE SINGLE
                </button>
                <button type="button" onClick={onCancel} data-testid="single-form-cancel" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-slate-300 text-xs font-semibold hover:border-bn-blue/50 transition-colors">
                    <X className="w-3.5 h-3.5" /> CANCEL
                </button>
            </div>
        </form>
    );
};

export default function AdminPage() {
    const [user, setUser] = useState(null);
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [busy, setBusy] = useState(false);

    const [computed, setComputed] = useState(null);
    const [doubles, setDoubles] = useState([]);
    const [base, setBase] = useState({ doubles: { total: "", wins: "", losses: "" }, singles: { total: "", wins: "", losses: "" } });
    const [displayLimits, setDisplayLimits] = useState({ singles: 20, doubles: 20 });
    const [freeTip, setFreeTip] = useState({ date: "", teams: "", pick: "", odds: "", note: "" });
    const [singles, setSingles] = useState([]);
    const [editingSingle, setEditingSingle] = useState(null);
    const [addingSingle, setAddingSingle] = useState(false);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [c, d, s] = await Promise.all([
                axios.get(`${API}/content`),
                axios.get(`${API}/doubles`),
                axios.get(`${API}/singles`),
            ]);
            if (c.data?.stats) setComputed(c.data);
            if (c.data?.stats_base) setBase(c.data.stats_base);
            if (c.data?.display_limits) setDisplayLimits(c.data.display_limits);
            if (c.data?.free_tip) setFreeTip(c.data.free_tip);
            setDoubles(Array.isArray(d.data) ? d.data : []);
            setSingles(Array.isArray(s.data) ? s.data : []);
        } catch {
            toast.error("Could not load site data.");
        }
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((res) => {
                setUser(res.data);
                loadAll();
            })
            .catch(() => setUser(false));
    }, [loadAll]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        setLoginError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
            setUser(data);
            loadAll();
        } catch (err) {
            setLoginError(fmtErr(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        setUser(false);
    };

    const saveContent = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/content`,
                {
                    stats_base: {
                        doubles: { total: Number(base.doubles.total), wins: Number(base.doubles.wins), losses: Number(base.doubles.losses) },
                        singles: { total: Number(base.singles.total), wins: Number(base.singles.wins), losses: Number(base.singles.losses) },
                    },
                    display_limits: { singles: Number(displayLimits.singles), doubles: Number(displayLimits.doubles) },
                    free_tip: freeTip,
                },
                { withCredentials: true }
            );
            toast.success("Base totals saved — statistics recalculated automatically.");
            loadAll();
        } catch (err) {
            if (err.response?.status === 401) return setUser(false);
            toast.error(fmtErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const saveDouble = async (d) => {
        setSaving(true);
        const payload = {
            date: d.date,
            status: d.status,
            matches: d.matches.map((m) => ({ teams: m.teams, pick: m.pick, odds: parseFloat(m.odds), score: m.score })),
        };
        try {
            if (d.double_id) {
                await axios.put(`${API}/admin/doubles/${d.double_id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/admin/doubles`, payload, { withCredentials: true });
            }
            toast.success(d.double_id ? "Double updated." : "Double added to the archive.");
            setEditing(null);
            setAdding(false);
            loadAll();
        } catch (err) {
            if (err.response?.status === 401) return setUser(false);
            toast.error(fmtErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const removeDouble = async (d) => {
        if (!window.confirm(`Delete the double from ${d.date}?`)) return;
        try {
            await axios.delete(`${API}/admin/doubles/${d.double_id}`, { withCredentials: true });
            toast.success("Double deleted.");
            loadAll();
        } catch (err) {
            if (err.response?.status === 401) return setUser(false);
            toast.error(fmtErr(err.response?.data?.detail));
        }
    };

    const saveSingle = async (s) => {
        setSaving(true);
        const nm = s.status === "nomatch";
        const payload = {
            date: s.date,
            status: s.status,
            teams: nm ? "—" : s.teams,
            pick: nm ? "—" : s.pick,
            odds: nm ? null : parseFloat(s.odds),
            score: nm ? "—" : s.score,
        };
        try {
            if (s.single_id) {
                await axios.put(`${API}/admin/singles/${s.single_id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/admin/singles`, payload, { withCredentials: true });
            }
            toast.success(s.single_id ? "Single updated." : "Single added to the archive.");
            setEditingSingle(null);
            setAddingSingle(false);
            loadAll();
        } catch (err) {
            if (err.response?.status === 401) return setUser(false);
            toast.error(fmtErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const removeSingle = async (s) => {
        if (!window.confirm(`Delete the single from ${s.date}?`)) return;
        try {
            await axios.delete(`${API}/admin/singles/${s.single_id}`, { withCredentials: true });
            toast.success("Single deleted.");
            loadAll();
        } catch (err) {
            if (err.response?.status === 401) return setUser(false);
            toast.error(fmtErr(err.response?.data?.detail));
        }
    };

    if (user === null) {
        return (
            <main className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-bn-blue animate-spin" />
            </main>
        );
    }

    if (user === false) {
        return (
            <main className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login-page">
                <div className="w-full max-w-sm bg-bn-card/70 border border-bn-line rounded-2xl p-8">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="w-8 h-8 rounded-lg bg-bn-blue/15 border border-bn-blue/40 flex items-center justify-center">
                            <Zap className="w-4 h-4 text-bn-blue" />
                        </span>
                        <span className="font-display font-extrabold tracking-tight text-lg text-white">
                            BETTING<span className="text-bn-blue">NATOR</span>
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-bn-green" /> Admin access only
                    </p>
                    <form onSubmit={login} className="space-y-4">
                        <div>
                            <label className={labelCls}>USERNAME</label>
                            <input data-testid="admin-login-email" type="text" required className={inputCls} value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="Your admin username" />
                        </div>
                        <div>
                            <label className={labelCls}>PASSWORD</label>
                            <input data-testid="admin-login-password" type="password" required className={inputCls} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" />
                        </div>
                        {loginError && (
                            <p data-testid="admin-login-error" className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
                                {loginError}
                            </p>
                        )}
                        <button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full py-3.5 rounded-full bg-bn-blue hover:bg-bn-bluehover disabled:opacity-60 text-white text-sm font-semibold tracking-wide transition-all">
                            {busy ? "SIGNING IN..." : "SIGN IN"}
                        </button>
                    </form>
                    <Link to="/" className="block mt-5 text-center text-xs text-slate-500 hover:text-bn-blue transition-colors">
                        ← Back to website
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen pb-24" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 bg-[#05070A]/90 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-[64px] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="font-display font-extrabold tracking-tight text-white">
                            BETTING<span className="text-bn-blue">NATOR</span>
                        </span>
                        <span className="font-mono text-[9px] tracking-[0.2em] px-2 py-1 rounded bg-bn-blue/15 text-bn-cyan border border-bn-blue/30">ADMIN PANEL</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-xs text-slate-400 hover:text-white transition-colors" data-testid="admin-view-site-link">
                            View site
                        </Link>
                        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors">
                            <LogOut className="w-3.5 h-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 space-y-10">
                <section className="bg-bn-card/60 border border-bn-line rounded-2xl p-6 sm:p-8" data-testid="admin-stats-section">
                    <h2 className="font-display font-bold text-xl text-white mb-2">Homepage Statistics</h2>
                    <p className="text-xs text-slate-500 leading-relaxed mb-6">
                        Statistics are calculated automatically from your archives — every WIN or LOSS you add, edit or delete updates the totals and win rates instantly. The Latest Single and Latest Double on the homepage are always the newest archive entries.
                    </p>

                    {computed && (
                        <div className="space-y-6 mb-8">
                            <div>
                                <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-3">OFFER 1 — SINGLES (AUTO-CALCULATED)</h3>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {[
                                        [computed.singles_stats.total_matches, "TOTAL MATCHES", "admin-computed-singles-total"],
                                        [computed.singles_stats.wins, "WINS", "admin-computed-singles-wins"],
                                        [computed.singles_stats.losses, "LOSSES", "admin-computed-singles-losses"],
                                        [`${computed.singles_stats.win_rate}%`, "WIN RATE", "admin-computed-singles-win-rate"],
                                    ].map(([v, l, id]) => (
                                        <div key={id} data-testid={id} className="bg-[#0B0F17] border border-bn-line rounded-lg px-4 py-3.5">
                                            <div className="font-display font-extrabold text-2xl text-white">{v}</div>
                                            <div className="font-mono text-[9px] tracking-[0.2em] text-slate-500 mt-1">{l}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-3">OFFER 2 — DOUBLES (AUTO-CALCULATED)</h3>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {[
                                        [computed.stats.total_doubles, "TOTAL DOUBLES", "admin-computed-doubles-total"],
                                        [computed.stats.wins, "WINS", "admin-computed-doubles-wins"],
                                        [computed.stats.losses, "LOSSES", "admin-computed-doubles-losses"],
                                        [`${computed.stats.win_rate}%`, "WIN RATE", "admin-computed-doubles-win-rate"],
                                    ].map(([v, l, id]) => (
                                        <div key={id} data-testid={id} className="bg-[#0B0F17] border border-bn-line rounded-lg px-4 py-3.5">
                                            <div className="font-display font-extrabold text-2xl text-white">{v}</div>
                                            <div className="font-mono text-[9px] tracking-[0.2em] text-slate-500 mt-1">{l}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={saveContent} className="space-y-6 pt-6 border-t border-bn-line">
                        <p className="text-xs text-slate-500 leading-relaxed">
                            Historical base totals — your results from before this archive (set once, rarely changed). Archive results are added on top automatically.
                        </p>
                        <div>
                            <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-4">DOUBLES BASE — BEFORE ARCHIVE</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelCls}>TOTAL</label>
                                    <input data-testid="admin-base-doubles-total" required type="number" min="0" className={inputCls} value={base.doubles.total} onChange={(e) => setBase({ ...base, doubles: { ...base.doubles, total: e.target.value } })} />
                                </div>
                                <div>
                                    <label className={labelCls}>WINS</label>
                                    <input data-testid="admin-base-doubles-wins" required type="number" min="0" className={inputCls} value={base.doubles.wins} onChange={(e) => setBase({ ...base, doubles: { ...base.doubles, wins: e.target.value } })} />
                                </div>
                                <div>
                                    <label className={labelCls}>LOSSES</label>
                                    <input data-testid="admin-base-doubles-losses" required type="number" min="0" className={inputCls} value={base.doubles.losses} onChange={(e) => setBase({ ...base, doubles: { ...base.doubles, losses: e.target.value } })} />
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-4">SINGLES BASE — BEFORE ARCHIVE</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelCls}>TOTAL</label>
                                    <input data-testid="admin-base-singles-total" required type="number" min="0" className={inputCls} value={base.singles.total} onChange={(e) => setBase({ ...base, singles: { ...base.singles, total: e.target.value } })} />
                                </div>
                                <div>
                                    <label className={labelCls}>WINS</label>
                                    <input data-testid="admin-base-singles-wins" required type="number" min="0" className={inputCls} value={base.singles.wins} onChange={(e) => setBase({ ...base, singles: { ...base.singles, wins: e.target.value } })} />
                                </div>
                                <div>
                                    <label className={labelCls}>LOSSES</label>
                                    <input data-testid="admin-base-singles-losses" required type="number" min="0" className={inputCls} value={base.singles.losses} onChange={(e) => setBase({ ...base, singles: { ...base.singles, losses: e.target.value } })} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-4">HOMEPAGE ARCHIVE SIZE</h3>
                            <div className="grid grid-cols-2 gap-4 max-w-md">
                                <div>
                                    <label className={labelCls}>SINGLES SHOWN</label>
                                    <input data-testid="admin-limit-singles" required type="number" min="1" max="100" className={inputCls} value={displayLimits.singles} onChange={(e) => setDisplayLimits({ ...displayLimits, singles: e.target.value })} />
                                </div>
                                <div>
                                    <label className={labelCls}>DOUBLES SHOWN</label>
                                    <input data-testid="admin-limit-doubles" required type="number" min="1" max="100" className={inputCls} value={displayLimits.doubles} onChange={(e) => setDisplayLimits({ ...displayLimits, doubles: e.target.value })} />
                                </div>
                            </div>
                            <p className="mt-2 text-[11px] text-slate-600">Controls the "latest N" text and how many cards are shown on the homepage for each offer.</p>
                        </div>

                        <div>
                            <h3 className="font-mono text-[11px] tracking-[0.25em] text-bn-cyan font-semibold mb-4">FREE DAILY TIP</h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>DATE</label>
                                    <input data-testid="admin-freetip-date" className={inputCls} value={freeTip.date} onChange={(e) => setFreeTip({ ...freeTip, date: e.target.value })} placeholder="14.09.2026" />
                                </div>
                                <div>
                                    <label className={labelCls}>MATCH</label>
                                    <input data-testid="admin-freetip-teams" className={inputCls} value={freeTip.teams} onChange={(e) => setFreeTip({ ...freeTip, teams: e.target.value })} placeholder="Team A vs Team B" />
                                </div>
                                <div>
                                    <label className={labelCls}>SELECTION</label>
                                    <input data-testid="admin-freetip-pick" className={inputCls} value={freeTip.pick} onChange={(e) => setFreeTip({ ...freeTip, pick: e.target.value })} placeholder="Over 2.5 Goals" />
                                </div>
                                <div>
                                    <label className={labelCls}>ODDS</label>
                                    <input data-testid="admin-freetip-odds" className={inputCls} value={freeTip.odds} onChange={(e) => setFreeTip({ ...freeTip, odds: e.target.value })} placeholder="2.10" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className={labelCls}>SHORT ANALYSIS (OPTIONAL)</label>
                                    <input data-testid="admin-freetip-note" className={inputCls} value={freeTip.note} onChange={(e) => setFreeTip({ ...freeTip, note: e.target.value })} placeholder="One short line of reasoning shown under the tip" />
                                </div>
                            </div>
                            <p className="mt-2 text-[11px] text-slate-600">Shown in the FREE DAILY TIP section on the homepage. Clear the match field to display the "being prepared" message instead.</p>
                        </div>

                        <button data-testid="admin-save-content-button" type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-bn-blue hover:bg-bn-bluehover disabled:opacity-60 text-white text-sm font-semibold transition-all">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE HOMEPAGE CONTENT
                        </button>
                    </form>
                </section>

                <section className="bg-bn-card/60 border border-bn-line rounded-2xl p-6 sm:p-8" data-testid="admin-archive-section">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-display font-bold text-xl text-white">Archive — Doubles ({doubles.length})</h2>
                        <button
                            data-testid="admin-add-double-button"
                            onClick={() => {
                                setAdding(true);
                                setEditing(null);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-xs font-semibold transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> ADD DOUBLE
                        </button>
                    </div>

                    {adding && (
                        <div className="mb-6">
                            <DoubleForm initial={emptyDouble()} onSave={saveDouble} onCancel={() => setAdding(false)} saving={saving} />
                        </div>
                    )}

                    <div className="space-y-3">
                        {doubles.map((d) => (
                            <div key={d.double_id} data-testid={`admin-double-row-${d.double_id}`} className="border border-bn-line rounded-xl p-4 bg-[#0B0F17]/60">
                                {editing?.double_id === d.double_id ? (
                                    <DoubleForm initial={d} onSave={saveDouble} onCancel={() => setEditing(null)} saving={saving} />
                                ) : (
                                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                        <span className="font-mono text-xs text-slate-400 w-24">{d.date}</span>
                                        <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded-full border ${d.status === "lost" ? "bg-red-400/10 text-red-400 border-red-400/30" : "bg-bn-green/10 text-bn-green border-bn-green/30"}`}>
                                            {d.status === "lost" ? "LOSS" : "WIN"}
                                        </span>
                                        <span className="text-sm text-slate-200 flex-1 min-w-[200px]">
                                            {d.matches[0].teams} · {d.matches[1].teams}
                                        </span>
                                        <span className="font-mono text-xs text-bn-cyan">@{combined(d)}</span>
                                        <div className="flex gap-2">
                                            <button onClick={() => { setEditing(d); setAdding(false); }} data-testid={`admin-edit-double-${d.double_id}`} className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-bn-blue/50 transition-colors" aria-label="Edit">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => removeDouble(d)} data-testid={`admin-delete-double-${d.double_id}`} className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {doubles.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No doubles in the archive yet — add your first one.</p>}
                    </div>
                </section>

                <section className="bg-bn-card/60 border border-bn-line rounded-2xl p-6 sm:p-8" data-testid="admin-singles-archive-section">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-display font-bold text-xl text-white">Archive — Singles ({singles.length})</h2>
                        <button
                            data-testid="admin-add-single-button"
                            onClick={() => {
                                setAddingSingle(true);
                                setEditingSingle(null);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-bn-blue hover:bg-bn-bluehover text-white text-xs font-semibold transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> ADD SINGLE
                        </button>
                    </div>

                    {addingSingle && (
                        <div className="mb-6">
                            <SingleForm initial={emptySingle()} onSave={saveSingle} onCancel={() => setAddingSingle(false)} saving={saving} />
                        </div>
                    )}

                    <div className="space-y-3">
                        {singles.map((s) => (
                            <div key={s.single_id} data-testid={`admin-single-row-${s.single_id}`} className="border border-bn-line rounded-xl p-4 bg-[#0B0F17]/60">
                                {editingSingle?.single_id === s.single_id ? (
                                    <SingleForm initial={s} onSave={saveSingle} onCancel={() => setEditingSingle(null)} saving={saving} />
                                ) : (
                                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                        <span className="font-mono text-xs text-slate-400 w-24">{s.date}</span>
                                        <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded-full border ${singleBadgeCls(s.status)}`}>
                                            {s.status === "lost" ? "LOSS" : s.status === "nomatch" ? "NO MATCH" : "WIN"}
                                        </span>
                                        <span className="text-sm text-slate-200 flex-1 min-w-[200px]">
                                            {s.teams}{s.status !== "nomatch" ? ` · ${s.pick}` : ""}
                                        </span>
                                        <span className="font-mono text-xs text-bn-cyan">{s.odds ? `@${Number(s.odds).toFixed(2)}` : "—"}</span>
                                        <div className="flex gap-2">
                                            <button onClick={() => { setEditingSingle(s); setAddingSingle(false); }} data-testid={`admin-edit-single-${s.single_id}`} className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-bn-blue/50 transition-colors" aria-label="Edit">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => removeSingle(s)} data-testid={`admin-delete-single-${s.single_id}`} className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {singles.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No singles in the archive yet — add your first one.</p>}
                    </div>
                </section>
            </div>
        </main>
    );
}
